import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavCounters, NavBadge } from "@/components/NavCounters";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/helpers";
import { Logo, LogoMark } from "@/components/Logo";
import { BRAND } from "@/config/brand";
import { CalendarRange, LayoutDashboard, ClipboardList, Users2, Hospital, Settings2, LogOut, Trophy, Ambulance, CreditCard, MoreHorizontal, X, CalendarClock, ScrollText, Car, UserPlus, Clock3 } from "lucide-react";
import { PushToggle } from "@/components/PushToggle";
import { NotificationCenter } from "@/components/NotificationCenter";
import { OrgStatusBanner } from "@/components/OrgStatusBanner";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [org, setOrg] = useState(null);
  const [more, setMore] = useState(false);
  const counters = useNavCounters(!!user && user.role !== "chauffeur");
  const COUNT_KEY = { "/app/demandes": "requests", "/app/rendez-vous": "appointments", "/app/vehicules": "vehicles", "/app/conges": "leaves", "/app/acces-patients": "patient_access" };

  useEffect(() => {
    const load = () => api.get("/organization").then((r) => setOrg(r.data)).catch(() => {});
    load();
    window.addEventListener("sanitdocs:org-updated", load);
    return () => window.removeEventListener("sanitdocs:org-updated", load);
  }, []);

  const links = [
    { to: "/app", end: true, icon: LayoutDashboard, label: "Tableau de bord", testid: "nav-dashboard" },
    { to: "/app/demandes", icon: ClipboardList, label: "Demandes", testid: "nav-requests" },
    { to: "/app/patients", icon: Users2, label: "Patients", testid: "nav-patients" },
    { to: "/app/nouveaux-clients", icon: UserPlus, label: "Nouveaux clients", testid: "nav-new-clients" },
    { to: "/app/etablissements", icon: Hospital, label: "Établissements", testid: "nav-establishments" },
    { to: "/app/rendez-vous", icon: CalendarClock, label: "Rendez-vous", testid: "nav-appointments" },
    { to: "/app/vehicules", icon: Car, label: "Véhicules", testid: "nav-vehicles" },
    { to: "/app/conges", icon: CalendarClock, label: "Congés & demandes", testid: "nav-leaves" },
    ...(org?.timesheets_enabled ? [{ to: "/app/heures", icon: Clock3, label: "Gestion des heures", testid: "nav-hours" }] : []),
    { to: "/app/planification", icon: CalendarRange, label: "Planification", testid: "nav-planning" },
    { to: "/app/acces-patients", icon: UserPlus, label: "Accès patients", testid: "nav-patient-access" },
    { to: "/app/points", icon: Trophy, label: "Points chauffeurs", testid: "nav-points" },
  ];
  // Mobile : 4 rubriques principales + « Plus » (le reste, avec badges ; total des actions cachées sur l'icône Plus).
  const MOBILE_PRIMARY = ["/app", "/app/demandes", "/app/rendez-vous", "/app/vehicules"];
  const mobilePrimary = links.filter((l) => MOBILE_PRIMARY.includes(l.to));
  const adminLinks = [
    { to: "/app/utilisateurs", icon: Ambulance, label: "Utilisateurs", testid: "nav-users" },
    { to: "/app/parametres", icon: Settings2, label: "Paramètres", testid: "nav-settings" },
    { to: "/app/journal-notifications", icon: ScrollText, label: "Journal notifications", testid: "nav-notification-log", admin: true },
    { to: "/app/abonnement", icon: CreditCard, label: "Abonnement & facturation", testid: "nav-subscription" },
  ];
  const moreLinks = [...links.filter((l) => !MOBILE_PRIMARY.includes(l.to)), ...(user?.role === "admin" ? adminLinks.map((l, i) => ({ ...l, adminSection: i === 0 })) : [])];
  const moreCount = moreLinks.reduce((n, l) => n + (counters[COUNT_KEY[l.to]] || 0), 0);


  return (
    <div className="flex w-full bg-background overflow-hidden" style={{ height: "100dvh", minHeight: "100dvh" }}>
      <aside data-testid="admin-sidebar" className="w-64 bg-brand-800 text-white flex-col shrink-0 hidden md:flex min-h-0 overflow-hidden">
        <div className="h-16 shrink-0 flex items-center px-5 border-b border-white/10"><Logo size={34} onDark /></div>
        <div className="px-5 py-3 shrink-0 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-brand-200 font-semibold">Entreprise</p>
          <p className="text-sm font-semibold truncate" data-testid="org-name">{org?.name || "…"}</p>
        </div>
        <nav data-testid="admin-sidebar-nav" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 space-y-1" style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y" }}>
          {links.map((l) => <NavItem key={l.to} {...l} count={counters[COUNT_KEY[l.to]]} />)}
          {user?.role === "admin" && (
            <>
              <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest font-semibold text-brand-300">Administration</p>
              {adminLinks.map((l) => <NavItem key={l.to} {...l} count={counters[COUNT_KEY[l.to]]} />)}
            </>
          )}
        </nav>
        <div className="p-3 shrink-0 border-t border-white/10" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <div className="px-3 py-2 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-accent-500 text-white font-heading font-bold flex items-center justify-center text-sm">{user?.first_name?.[0]}{user?.last_name?.[0]}</div>
            <div className="min-w-0"><p className="font-semibold text-sm truncate">{user?.first_name} {user?.last_name}</p><p className="text-xs text-brand-200">{ROLE_LABEL[user?.role]}</p></div>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-brand-100 hover:bg-white/10 transition-colors text-sm font-semibold">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 shrink-0">
          <div className="md:hidden"><Logo size={30} /></div>
          <p className="hidden md:block text-sm text-slate-500 font-medium">{BRAND.tagline}</p>
          <div className="flex items-center gap-2">
            <PushToggle variant="icon" />
            <NotificationCenter role={user?.role} />
            <div className="md:hidden">
              <button data-testid="admin-logout-mobile" onClick={() => { logout(); nav("/login"); }} className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><LogOut className="w-4 h-4" /></button>
            </div>
          </div>
        </header>
        <OrgStatusBanner />
        <main className="flex-1 overflow-auto p-4 md:p-8 animate-fade-in"><Outlet /></main>
        <nav data-testid="mobile-nav" className="md:hidden bg-white border-t border-slate-200 flex justify-around py-1.5" style={{ paddingBottom: "max(0.375rem, env(safe-area-inset-bottom))" }}>
          {mobilePrimary.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`${l.testid}-mobile`} className={({ isActive }) => `flex-1 min-w-0 flex flex-col items-center gap-0.5 px-1 py-1 text-[11px] font-semibold ${isActive ? "text-accent-700" : "text-slate-400"}`}>
              <span className="relative inline-flex"><l.icon className="w-6 h-6" /><NavBadge n={counters[COUNT_KEY[l.to]]} testid={`${l.testid}-badge-mobile`} mobile /></span><span className="truncate max-w-full">{l.label.split(" ")[0]}</span>
            </NavLink>
          ))}
          <button data-testid="nav-more" onClick={() => setMore(true)}
            className={`flex-1 min-w-0 flex flex-col items-center gap-0.5 px-1 py-1 text-[11px] font-semibold ${moreLinks.some((l) => location.pathname.startsWith(l.to)) ? "text-accent-700" : "text-slate-400"}`}>
            <span className="relative inline-flex"><MoreHorizontal className="w-6 h-6" /><NavBadge n={moreCount} testid="nav-more-badge" mobile /></span>Plus
          </button>
        </nav>
        {more && (
          <div data-testid="more-menu" className="md:hidden fixed inset-0 z-50 flex flex-col justify-end" onClick={() => setMore(false)}>
            <div className="absolute inset-0 bg-brand-900/40" />
            <div className="relative bg-white rounded-t-3xl p-4 shadow-2xl animate-fade-in max-h-[85vh] overflow-y-auto" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 px-2">Autres rubriques</p>
                <button data-testid="more-close" aria-label="Fermer" onClick={() => setMore(false)} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
              </div>
              {moreLinks.map((l, i) => (
                <div key={l.to}>
                  {l.adminSection && <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 px-2 pt-3 pb-1">Administration</p>}
                  <NavLink to={l.to} data-testid={`${l.testid}-mobile`} onClick={() => setMore(false)}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl font-semibold text-base ${isActive ? "bg-accent-50 text-accent-800" : "text-slate-700 active:bg-slate-50"}`}>
                    <l.icon className="w-5 h-5" />{l.label}<NavBadge n={counters[COUNT_KEY[l.to]]} testid={`${l.testid}-badge-more`} dark />
                  </NavLink>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NavItem({ to, end, icon: Icon, label, testid, count }) {
  return (
    <NavLink to={to} end={end} data-testid={testid}
      className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-accent-500 text-white shadow-sm shadow-accent-900/30" : "text-brand-100 hover:bg-white/10 hover:text-white"}`}>
      <Icon className="w-5 h-5" /> {label}<NavBadge n={count} testid={`${testid}-badge`} />
    </NavLink>
  );
}
